import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkle, Brain, ChatCircleDots, ArrowRight, Warning, CheckCircle,
  UserCircle, Lightning, Megaphone, PencilLine, Target, ClockCounterClockwise,
} from "@phosphor-icons/react";

// AI Center — Tier 1 + Tier 2. Presentation-only overview that ties together
// the AI features that ALREADY exist (Assistant + Insights + Agent
// Instructions) + a chronological feed of recent AI activity from the
// ai_activity_log collection. Zero new AI capability.

const FEATURE_META = {
  ai_assistant:      { label: "AI Assistant",      Icon: ChatCircleDots, tone: "text-white/90" },
  campaign_strategy: { label: "Campaign Strategy", Icon: Megaphone,      tone: "text-emerald-300" },
  content_draft:     { label: "Content Draft",     Icon: PencilLine,     tone: "text-blue-300" },
  lead_scoring:      { label: "Lead Scoring",      Icon: Target,         tone: "text-amber-300" },
  ai_insights:       { label: "AI Insights",       Icon: Lightning,      tone: "text-fuchsia-300" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function AICenter() {
  const { currentOrg } = useAuth();
  const [agent, setAgent] = useState(null);
  const [insights, setInsights] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/activity", { params: { limit: 20 } });
      setActivity(data || []);
    } catch { setActivity([]); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentR, insightsR] = await Promise.all([
        api.get("/ai/agent-status").catch(() => ({ data: null })),
        api.get("/dashboard/ai-insights").catch(() => ({ data: null })),
        loadActivity(),
      ]);
      setAgent(agentR.data);
      setInsights(insightsR.data);
    } finally { setLoading(false); }
  }, [loadActivity]);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/dashboard/ai-insights/generate");
      setInsights(data);
      if (data?.insufficient_data) {
        toast.info("Not enough data yet — add a few leads or a customer first.");
      } else {
        toast.success("Fresh insights generated");
      }
      // Refresh activity feed — the generate call itself just added a log entry.
      loadActivity();
    } catch {
      toast.error("Couldn't generate insights right now");
    } finally { setGenerating(false); }
  };

  const insightItems = insights?.items || [];
  const insufficient = !!insights?.insufficient_data;
  const insightTs = insights?.generated_at ? new Date(insights.generated_at) : null;
  const activityRows = activity || [];

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="ai-center-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Brain weight="duotone" className="h-3 w-3" /> AI Center
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Overview</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            One place for the AI features already active in this workspace — your Assistant,
            the growth intelligence it surfaces, and a direct line to ask it anything.
          </p>
        </div>
        <Link
          to="/assistant"
          data-testid="ai-center-ask-cta"
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
        >
          <ChatCircleDots weight="fill" className="h-4 w-4" />
          Ask AI
          <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Grid: Agent card + Ask AI card side by side, Insights below */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── AI Agent card ─────────────────────────────────────────────── */}
        <section
          data-testid="ai-center-agent-card"
          className="lg:col-span-2 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-md bg-graphite-950 border border-white/10 grid place-items-center shrink-0">
              <Sparkle weight="fill" className="h-5 w-5 text-white/90" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Your AI Agent</div>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-2xl tracking-tight" data-testid="ai-center-agent-name">
                  {agent?.agent_name || "AI Assistant"}
                </h2>
                <span
                  data-testid="ai-center-agent-status"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/5 text-emerald-200"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  Ready
                </span>
              </div>
              <p className="mt-2 text-sm text-ink-secondary max-w-xl">
                Grounded on this workspace's knowledge base, leads, customers, campaigns and partners.
                Cites its sources so you can verify.
              </p>
            </div>
          </div>

          {/* Persona / instructions block */}
          <div
            data-testid="ai-center-agent-persona"
            className="mt-6 rounded-md border border-white/10 bg-graphite-950/40 p-4"
          >
            <div className="flex items-start gap-3">
              <UserCircle weight="duotone" className="h-4 w-4 text-ink-secondary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.15em] text-ink-secondary">
                  Agent persona
                </div>
                {loading ? (
                  <div className="mt-1 text-sm text-ink-secondary">Loading…</div>
                ) : agent?.has_custom_instructions ? (
                  <div className="mt-1" data-testid="ai-center-agent-persona-custom">
                    <div className="flex items-center gap-2 text-sm text-ink-primary">
                      <CheckCircle weight="fill" className="h-3.5 w-3.5 text-emerald-300" />
                      Custom instructions configured
                    </div>
                    <p className="mt-1 text-xs text-ink-secondary">
                      A workspace-specific persona ({agent.custom_instructions_length} chars) is
                      shaping this agent's tone and priorities. Ask your platform admin to review or
                      revise in the Platform panel.
                    </p>
                  </div>
                ) : (
                  <div className="mt-1" data-testid="ai-center-agent-persona-default">
                    <div className="flex items-center gap-2 text-sm text-ink-primary">
                      <Warning weight="fill" className="h-3.5 w-3.5 text-amber-300/90" />
                      Using default persona
                    </div>
                    <p className="mt-1 text-xs text-ink-secondary">
                      No custom instructions configured for this workspace. Your platform admin can
                      add tone, style and priority guidance from the Platform panel to make replies
                      feel more on-brand.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Ask AI shortcut ───────────────────────────────────────────── */}
        <section
          data-testid="ai-center-ask-card"
          className="rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8 flex flex-col"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
            <ChatCircleDots weight="duotone" className="h-3 w-3" /> Chat
          </div>
          <h3 className="mt-2 font-display text-xl tracking-tight">Ask AI anything</h3>
          <p className="mt-2 text-sm text-ink-secondary flex-1">
            Open a fresh conversation with the Assistant. Ask for a campaign brief,
            summarize this quarter's leads, or draft a follow-up email — grounded on
            your workspace data.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Link
              to="/assistant"
              data-testid="ai-center-ask-primary"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              <ChatCircleDots weight="fill" className="h-4 w-4" />
              Open AI Assistant
            </Link>
            <div className="text-[11px] text-ink-secondary/70 text-center">
              Powered by Claude · Grounded on your workspace
            </div>
          </div>
        </section>

        {/* ── AI Insights (reuses same endpoints as Dashboard widget) ───── */}
        <section
          data-testid="ai-center-insights"
          className="lg:col-span-3 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                <Lightning weight="duotone" className="h-3 w-3" /> Growth intelligence
              </div>
              <h3 className="mt-1 font-display text-xl tracking-tight">AI Insights</h3>
              {insightTs && (
                <div className="mt-1 text-[11px] text-ink-secondary" data-testid="ai-center-insights-ts">
                  Last analyzed: {insightTs.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </div>
              )}
            </div>
            <button
              data-testid="ai-center-insights-generate"
              onClick={generate}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 hover:bg-neutral-200 disabled:opacity-60 px-4 py-2 text-sm font-medium transition-colors"
            >
              <Sparkle weight="fill" className="h-3.5 w-3.5" />
              {generating ? "Analyzing…" : insightTs ? "Regenerate" : "Generate insights"}
            </button>
          </div>

          {loading ? (
            <div className="mt-6 text-sm text-ink-secondary">Loading insights…</div>
          ) : insufficient ? (
            <div className="mt-6 text-sm text-ink-secondary" data-testid="ai-center-insights-empty">
              Not enough data yet — add a few leads, a customer, or an active campaign, then try again.
            </div>
          ) : insightItems.length === 0 ? (
            <div className="mt-6 text-sm text-ink-secondary" data-testid="ai-center-insights-idle">
              Click <span className="text-ink-primary">Generate insights</span> to have Claude
              review this workspace and surface 3–5 grounded observations.
            </div>
          ) : (
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ai-center-insights-list">
              {insightItems.map((it, i) => (
                <li
                  key={i}
                  data-testid={`ai-center-insights-item-${i}`}
                  className="flex items-start gap-3 rounded-md border border-white/10 bg-graphite-950/40 px-4 py-3 text-sm text-ink-primary leading-relaxed"
                >
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                  <span className="min-w-0">{it}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── Recent AI Activity (Tier 2) ────────────────────────────────── */}
        <section
          data-testid="ai-center-activity"
          className="lg:col-span-3 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                <ClockCounterClockwise weight="duotone" className="h-3 w-3" /> Recent
              </div>
              <h3 className="mt-1 font-display text-xl tracking-tight">AI Activity</h3>
              <p className="mt-1 text-[11px] text-ink-secondary">
                Every AI action taken in this workspace — assistant chats, campaign strategies,
                content drafts, lead scoring, and insight generation.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="mt-5 text-sm text-ink-secondary">Loading activity…</div>
          ) : activityRows.length === 0 ? (
            <div className="mt-5 text-sm text-ink-secondary" data-testid="ai-center-activity-empty">
              No AI activity yet. Ask the assistant, score a lead, or generate insights to see entries here.
            </div>
          ) : (
            <ol className="mt-5 space-y-2" data-testid="ai-center-activity-list">
              {activityRows.map((row) => {
                const meta = FEATURE_META[row.feature] || {
                  label: row.feature, Icon: Sparkle, tone: "text-ink-primary",
                };
                const Icon = meta.Icon;
                return (
                  <li
                    key={row.log_id}
                    data-testid={`ai-center-activity-row-${row.log_id}`}
                    className="flex items-start gap-3 rounded-md border border-white/10 bg-graphite-950/40 px-4 py-2.5 hover:bg-graphite-950/60 transition-colors"
                  >
                    <div className="h-6 w-6 rounded-sm bg-graphite-950 border border-white/10 grid place-items-center shrink-0 mt-0.5">
                      <Icon weight="duotone" className={`h-3.5 w-3.5 ${meta.tone}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-ink-secondary/70">
                        {meta.label}
                      </div>
                      <div
                        className="text-sm text-ink-primary truncate"
                        data-testid={`ai-center-activity-summary-${row.log_id}`}
                      >
                        {row.summary || "(no summary)"}
                      </div>
                    </div>
                    <div
                      className="text-[11px] text-ink-secondary shrink-0 tabular-nums"
                      title={row.timestamp}
                      data-testid={`ai-center-activity-time-${row.log_id}`}
                    >
                      {relativeTime(row.timestamp)}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
